import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { isDefined, last, omit } from 'remeda';
import { v4 } from 'uuid';
import { z } from 'zod/v4';

import { type ApiError, addResourceNameToQueryResult, useAxiosInstance } from './utils';

const DebtorBalanceParamsSchema = z.object({
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  types: z.array(z.string()),
  showSettled: z.boolean(),
});

const ApiAmountSchema = z.preprocess(val => Number(val ?? 0), z.number());

const MatchedCreditNoteSchema = z.object({
  reference: z.string(),
  issueDate: z
    .string()
    .transform(date => dayjs(date))
    .optional(),
  amount: ApiAmountSchema,
});

const PaymentSchema = z
  .object({
    amount: ApiAmountSchema,
    remainingBalance: ApiAmountSchema,
    date: z.string().transform(date => dayjs(date)),
    communication: z.string().nullable(),
    paymentType: z.string().nullable(),
    paymentId: z.number().nullable(),
  })
  .transform(data => ({
    ...omit(data, ['date']),
    issueDate: data.date,
    id: v4(),
  }));

const MatchedPaymentSchema = z
  .object({
    amount: ApiAmountSchema,
    remainingBalance: ApiAmountSchema,
    date: z
      .string()
      .transform(date => dayjs(date))
      .nullable(),
    communication: z.string().nullable(),
    isLoss: z.boolean(),
  })
  .transform(data => ({
    ...omit(data, ['date']),
    issueDate: data.date,
  }));

const InvoiceSchema = z.object({
  id: z.number(),
  reference: z.string().nullable().default(''),
  totalTvac: ApiAmountSchema,
  totalHtva: ApiAmountSchema,
  remainingBalance: ApiAmountSchema,
  remainingLateFees: ApiAmountSchema,
  lateFees: ApiAmountSchema,
  penaltyClauseAmount: ApiAmountSchema,
  issueDate: z.string().transform(date => dayjs(date)),
  dueDate: z.string(),
  status: z.string(),
  matchedPayments: z.array(MatchedPaymentSchema),
  creditNotes: z.array(MatchedCreditNoteSchema),
  lifecycleState: z.string(),
});

const CreditNoteSchema = InvoiceSchema.pick({
  id: true,
  reference: true,
  totalTvac: true,
  totalHtva: true,
  remainingBalance: true,
  issueDate: true,
  dueDate: true,
  status: true,
  lifecycleState: true,
}).extend({
  invoices: z.array(
    z.object({
      amount: z.coerce.number(),
      reference: z.string(),
      isLoss: z.boolean(),
      date: z.string().transform(date => dayjs(date)),
    }),
  ),
  payments: z.array(PaymentSchema).optional(),
});

export const DebtorBalanceSchema = z.object({
  invoices: z.array(InvoiceSchema),
  payments: z.array(PaymentSchema),
  creditNotes: z.array(CreditNoteSchema),
});

export type DebtorBalance = z.infer<typeof DebtorBalanceSchema>;
export type DebtorBalanceInvoice = z.infer<typeof InvoiceSchema>;
export type DebtorBalancePayment = z.infer<typeof PaymentSchema>;
export type DebtorBalanceMatchedPayment = z.infer<typeof PaymentSchema>;
export type DebtorBalanceCreditNote = z.infer<typeof CreditNoteSchema>;
export type DebtorBalanceParams = z.infer<typeof DebtorBalanceParamsSchema>;

export async function loadDebtorBalanceFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;
  const showSettled = last(queryKey);

  const { data } = await instance.get(`/debtors/${queryKey[1]}/balance?showSettled=${showSettled}`);
  return DebtorBalanceSchema.parse(data);
}

const emptyDebtorBalance: DebtorBalance = { invoices: [], payments: [], creditNotes: [] };

export function useLoadDebtorBalance(debtorId: string, showSettled?: boolean) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<DebtorBalance, ApiError>({
    queryKey: ['debtors', debtorId, 'balance', showSettled ?? false] as const,
    queryFn: context => loadDebtorBalanceFn(axiosInstance, context),
    placeholderData: previousData => {
      if (!isDefined(previousData)) {
        return {
          invoices: [],
          payments: [],
          creditNotes: [],
        };
      }

      return previousData;
    },
  });

  return addResourceNameToQueryResult<DebtorBalance, ApiError, 'debtorBalance', 'PreventUndefined'>(
    'debtorBalance',
    queryResult,
    emptyDebtorBalance,
  );
}
